// ─── End-to-End Encryption — crypto primitives for Secure Chat ───────────────
// See CHAT_E2E_DESIGN.md (repo root) for the full design. This module is the
// ONLY place in the frontend that touches libsodium directly — every other
// chat file (key setup/unlock UI, message send/receive pipeline, attachment
// encryption) calls through these functions so the actual cryptographic
// operations live in one small, auditable place.
//
// Hard rule this module exists to uphold: plaintext (message text, file
// bytes, private keys, the recovery passphrase) must never leave the
// browser. This file makes no network calls and never imports firebase/* —
// it is pure crypto, deliberately kept dependency-free of the rest of the
// app so it's easy to audit in isolation.
//
// All binary values (keys, nonces, salts, ciphertext) are passed and
// returned as base64 strings, so they drop straight into Firestore string
// fields and React state without any extra encoding step at call sites.

// IMPORTANT: this must be the "sumo" build, not plain 'libsodium-wrappers'.
// The lite build silently omits crypto_pwhash (Argon2id) and its constants —
// sodium.crypto_pwhash_SALTBYTES etc. come back `undefined` rather than
// throwing on import, so the failure doesn't surface until
// wrapPrivateKeyWithPassphrase()/derivePassphraseKey() below actually calls
// sodium.randombytes_buf(undefined), which throws libsodium-wrappers' own
// "length cannot be null or undefined". That was a real bug here: every
// chat key setup/reset call hit this, 100% of the time, because the lite
// package was installed (see frontend/package.json — now fixed to depend
// on 'libsodium-wrappers-sumo' instead).
import sodium from 'libsodium-wrappers-sumo';

// Resolves once the underlying WASM build of libsodium has finished
// loading. Safe to call any number of times — `sodium.ready` is memoized
// inside the library itself, so awaiting it after the first time resolves
// immediately. Every exported function below awaits this internally, so
// callers never have to remember to do it themselves; you can also call it
// once eagerly (e.g. when the chat sidebar mounts) to avoid a first-call
// delay.
export async function ready() {
  await sodium.ready;
  return sodium;
}

function b64(bytes) {
  return sodium.to_base64(bytes, sodium.base64_variants.ORIGINAL);
}
function unb64(str) {
  return sodium.from_base64(str, sodium.base64_variants.ORIGINAL);
}

// ─── 1. Identity keypair (X25519) ─────────────────────────────────────────────
// One per user, generated once in-browser on first chat use (design doc §3).
// The public half is uploaded to chatKeys/{userId} — safe to share company-
// wide, since anyone encrypting TO this user needs it. The private half must
// never be transmitted in plaintext; see wrapPrivateKeyWithPassphrase below
// for the only way it's ever allowed to leave this device.
export async function generateIdentityKeypair() {
  await sodium.ready;
  const kp = sodium.crypto_box_keypair();
  return {
    publicKey: b64(kp.publicKey),
    privateKey: b64(kp.privateKey),
  };
}

// ─── 2. One-time symmetric keys (per message, per file) ──────────────────────
// `mk` in the design doc for a message, `fk` for a file attachment — same
// primitive either way, generated fresh every time so a single compromised
// key only ever exposes the one message/file it belongs to, never a whole
// thread's history.
export async function generateSymmetricKey() {
  await sodium.ready;
  return b64(sodium.crypto_secretbox_keygen());
}

// ─── 3. Encrypt/decrypt content with a symmetric key (crypto_secretbox) ──────
// Used directly for message bodies; the attachments module (#150) layers
// crypto_secretstream chunking on top of the same underlying key concept for
// large files. `plaintext` may be a string or a Uint8Array — the return
// ciphertext/nonce are always base64 strings ready to store in Firestore.
export async function secretboxEncrypt(plaintext, keyB64) {
  await sodium.ready;
  const key = unb64(keyB64);
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const msgBytes = typeof plaintext === 'string' ? sodium.from_string(plaintext) : plaintext;
  const ciphertext = sodium.crypto_secretbox_easy(msgBytes, nonce, key);
  return { ciphertext: b64(ciphertext), nonce: b64(nonce) };
}

// Throws (never returns null/undefined) on a wrong key or tampered
// ciphertext, so call sites can render a clear "couldn't decrypt this
// message" state instead of silently showing garbage. Pass `asText: false`
// to get the raw Uint8Array back (used for binary attachment chunks).
export async function secretboxDecrypt(ciphertextB64, nonceB64, keyB64, { asText = true } = {}) {
  await sodium.ready;
  const key = unb64(keyB64);
  const nonce = unb64(nonceB64);
  const ciphertext = unb64(ciphertextB64);
  const opened = sodium.crypto_secretbox_open_easy(ciphertext, nonce, key);
  if (opened === null) throw new Error('Decryption failed — wrong key or tampered ciphertext');
  return asText ? sodium.to_string(opened) : opened;
}

// ─── 4. Wrapping a symmetric key to a specific recipient (crypto_box) ────────
// This is how `mk`/`fk` is delivered to each thread member individually
// (design doc §4): the sender encrypts the raw key bytes to the recipient's
// public key using their OWN private key, so crypto_box's built-in
// authentication lets the recipient be sure the wrapped key really came from
// that sender's keypair (not just "someone who knows the recipient's public
// key," which is public information anyway).
export async function boxWrapKey(rawKeyB64, recipientPublicKeyB64, senderPrivateKeyB64) {
  await sodium.ready;
  const rawKey = unb64(rawKeyB64);
  const recipientPublicKey = unb64(recipientPublicKeyB64);
  const senderPrivateKey = unb64(senderPrivateKeyB64);
  const nonce = sodium.randombytes_buf(sodium.crypto_box_NONCEBYTES);
  const wrapped = sodium.crypto_box_easy(rawKey, nonce, recipientPublicKey, senderPrivateKey);
  return { wrappedKey: b64(wrapped), nonce: b64(nonce) };
}

// Reverses boxWrapKey. Throws on failure (wrong keypair, or the ciphertext
// was tampered with) rather than returning null, for the same reason as
// secretboxDecrypt above.
export async function boxUnwrapKey(wrappedKeyB64, nonceB64, senderPublicKeyB64, recipientPrivateKeyB64) {
  await sodium.ready;
  const wrapped = unb64(wrappedKeyB64);
  const nonce = unb64(nonceB64);
  const senderPublicKey = unb64(senderPublicKeyB64);
  const recipientPrivateKey = unb64(recipientPrivateKeyB64);
  const rawKey = sodium.crypto_box_open_easy(wrapped, nonce, senderPublicKey, recipientPrivateKey);
  if (rawKey === null) throw new Error('Key unwrap failed — wrong keypair or tampered data');
  return b64(rawKey);
}

// ─── 5. Passphrase-based wrap/unwrap of the private key (recovery backup) ────
// crypto_pwhash (Argon2id) turns the user's Chat Recovery Passphrase — a
// secret deliberately separate from their login password (design doc §3,
// so an admin resetting the login password can never indirectly reach the
// private key) — into a symmetric key; that key then wraps the private key
// with crypto_secretbox for storage in chatKeyBackups/{userId}. The
// passphrase itself is never transmitted or stored anywhere, in any form —
// only this module ever sees it, for the few milliseconds it takes to
// derive the wrapping key, and then it's gone.
//
// INTERACTIVE ops/mem limits are libsodium's preset for "unlock on my own
// device, the user is waiting" — appropriate here, but explicitly NOT
// hardened against an offline attacker with a stolen backup blob and
// unlimited compute forever. A higher MODERATE/SENSITIVE preset would make
// every unlock noticeably slower; flagged in the design doc as an accepted
// trade-off for v1, not an oversight.
async function derivePassphraseKey(passphrase, saltB64) {
  await sodium.ready;
  const salt = unb64(saltB64);
  return sodium.crypto_pwhash(
    sodium.crypto_secretbox_KEYBYTES,
    sodium.from_string(passphrase),
    salt,
    sodium.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    sodium.crypto_pwhash_ALG_ARGON2ID13
  );
}

// Encrypts the user's private key under a key derived from their recovery
// passphrase. Returns exactly the three fields chatKeyBackups/{userId} needs
// to store (salt, nonce, encryptedPrivateKey) — none of which are useful to
// anyone, including us, without the passphrase.
export async function wrapPrivateKeyWithPassphrase(privateKeyB64, passphrase) {
  await sodium.ready;
  const salt = sodium.randombytes_buf(sodium.crypto_pwhash_SALTBYTES);
  const saltB64 = b64(salt);
  const wrappingKey = await derivePassphraseKey(passphrase, saltB64);
  const nonce = sodium.randombytes_buf(sodium.crypto_secretbox_NONCEBYTES);
  const encrypted = sodium.crypto_secretbox_easy(unb64(privateKeyB64), nonce, wrappingKey);
  return {
    salt: saltB64,
    nonce: b64(nonce),
    encryptedPrivateKey: b64(encrypted),
  };
}

// Reverses wrapPrivateKeyWithPassphrase entirely client-side. Throws (rather
// than returning null) on a wrong passphrase, so the "Unlock your chat"
// modal (#147) can show "incorrect passphrase" with zero server round-trip
// — there is no oracle here that could help an attacker distinguish "wrong
// passphrase" from "server error" or leak timing about which character
// differed.
export async function unwrapPrivateKeyWithPassphrase(backup, passphrase) {
  await sodium.ready;
  const { salt, nonce, encryptedPrivateKey } = backup;
  const wrappingKey = await derivePassphraseKey(passphrase, salt);
  const opened = sodium.crypto_secretbox_open_easy(unb64(encryptedPrivateKey), unb64(nonce), wrappingKey);
  if (opened === null) throw new Error('Incorrect recovery passphrase');
  return b64(opened);
}

// ─── 6. Streamed file encryption (crypto_secretstream) ───────────────────────
// Used for attachment bytes (design doc §5, Task #150). The same one-time-key
// concept as generateSymmetricKey() above (`fk` rather than `mk` in the
// design doc's naming) — crypto_secretbox_keygen and
// crypto_secretstream_xchacha20poly1305_keygen both produce 32-byte keys, so
// generateSymmetricKey() is reused as-is for file keys; no separate keygen
// needed. Filename encryption also reuses secretboxEncrypt/secretboxDecrypt
// directly with this same `fk` (see encryptedAttachments.js) — only the file
// BYTES need the chunked secretstream treatment below, since a filename is
// always small enough for one ordinary secretbox call.
//
// Processing happens in fixed-size plaintext chunks so a large file is never
// handed to a single crypto call as one giant buffer, and each chunk is
// independently authenticated (tamper in one chunk fails right there, not
// silently). The encrypted output is one framed Uint8Array: the stream
// header, then each chunk as [4-byte big-endian length][ciphertext bytes].
// Firebase Storage's upload API takes a single Blob/Uint8Array rather than a
// true backpressured stream, so the final encrypted result is still
// assembled in memory before upload — chunking here bounds *processing* size,
// not the final upload's memory footprint (capped at 50MB by storage.rules
// regardless, see Task #146).
const FILE_CHUNK_SIZE = 1024 * 1024; // 1 MiB of plaintext per chunk

function concatUint8Arrays(parts) {
  const total = parts.reduce((sum, p) => sum + p.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const p of parts) {
    out.set(p, offset);
    offset += p.length;
  }
  return out;
}

// `plainBytes` must be a Uint8Array. `onProgress`, if given, is called with a
// 0–1 fraction as chunks are processed. Returns a single Uint8Array ready to
// upload as-is.
export async function encryptFileBytes(plainBytes, keyB64, onProgress) {
  await sodium.ready;
  const key = unb64(keyB64);
  const { state, header } = sodium.crypto_secretstream_xchacha20poly1305_init_push(key);
  const TAG = sodium.crypto_secretstream_xchacha20poly1305_tag;

  const total = plainBytes.byteLength;
  const parts = [header];
  let offset = 0;
  while (offset < total || total === 0) {
    const end = Math.min(offset + FILE_CHUNK_SIZE, total);
    const chunk = plainBytes.subarray(offset, end);
    const isLast = end >= total;
    const encryptedChunk = sodium.crypto_secretstream_xchacha20poly1305_push(
      state, chunk, null, isLast ? TAG.FINAL : TAG.MESSAGE
    );
    const lenPrefix = new Uint8Array(4);
    new DataView(lenPrefix.buffer).setUint32(0, encryptedChunk.length, false);
    parts.push(lenPrefix, encryptedChunk);
    offset = end;
    if (onProgress) onProgress(total === 0 ? 1 : offset / total);
    if (total === 0) break; // empty file — still emit one FINAL chunk, then stop
  }
  return concatUint8Arrays(parts);
}

// Reverses encryptFileBytes. `encryptedBytes` must be a Uint8Array (Firebase
// Storage's getBytes() returns an ArrayBuffer — wrap it with
// `new Uint8Array(arrayBuffer)` before calling this). Throws on a wrong key,
// truncated data, or any tampered chunk — never returns partial/garbage
// output, for the same reason as secretboxDecrypt above.
export async function decryptFileBytes(encryptedBytes, keyB64) {
  await sodium.ready;
  const key = unb64(keyB64);
  const TAG = sodium.crypto_secretstream_xchacha20poly1305_tag;
  const headerLen = sodium.crypto_secretstream_xchacha20poly1305_HEADERBYTES;

  if (encryptedBytes.length < headerLen) throw new Error('Attachment data is truncated');
  let offset = 0;
  const header = encryptedBytes.subarray(offset, offset + headerLen);
  offset += headerLen;
  const state = sodium.crypto_secretstream_xchacha20poly1305_init_pull(header, key);

  const plainParts = [];
  let reachedFinal = false;
  while (offset < encryptedBytes.length) {
    if (offset + 4 > encryptedBytes.length) throw new Error('Attachment data is truncated');
    const chunkLen = new DataView(
      encryptedBytes.buffer, encryptedBytes.byteOffset + offset, 4
    ).getUint32(0, false);
    offset += 4;
    if (offset + chunkLen > encryptedBytes.length) throw new Error('Attachment data is truncated');
    const chunkCiphertext = encryptedBytes.subarray(offset, offset + chunkLen);
    offset += chunkLen;
    const result = sodium.crypto_secretstream_xchacha20poly1305_pull(state, chunkCiphertext);
    if (result === false) throw new Error('Attachment decryption failed — wrong key or tampered data');
    plainParts.push(result.message);
    if (result.tag === TAG.FINAL) reachedFinal = true;
  }
  if (!reachedFinal) throw new Error('Attachment data is incomplete or was truncated');
  return concatUint8Arrays(plainParts);
}

// ─── 7. Misc helpers ──────────────────────────────────────────────────────────
// A short, stable, non-secret fingerprint of a public key. Not wired into
// any UI yet — kept here for the future "verify contact" / safety-number
// affordance flagged as a known limitation in the design doc (§9.2), so
// when that's built it doesn't need a new crypto primitive, just a UI.
export async function shortFingerprint(publicKeyB64) {
  await sodium.ready;
  const hash = sodium.crypto_generichash(8, unb64(publicKeyB64));
  return sodium.to_hex(hash);
}
